import React, { useEffect, useMemo, useState } from 'react';
import { getAllEmployees } from '../services/employeeService';
import { runPayroll, getPayrollReport } from '../services/payrollService';
import { Search, CheckCircle2 } from 'lucide-react';
import AdminLayout from '../components/AdminLayout';
import '../styles/tailwind.css';

const isCreditedStatus = (status) =>
  ['credited', 'amount credited', 'amount_credited', 'paid', 'payment credited']
    .includes(String(status || '').trim().toLowerCase());

// Payroll can only be run for employees with a CTC or basic salary in their salary details.
const hasSalary = (employee) => Number(employee.ctc) > 0 || Number(employee.basicSalary) > 0;

const fullName = (employee) => `${employee.firstName || ''} ${employee.lastName || ''}`.trim() || 'N/A';

function PayrollPage({ userName, onLogout }) {
  const [employees, setEmployees] = useState([]);
  const [includedEmployeeIds, setIncludedEmployeeIds] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [excludedSearchQuery, setExcludedSearchQuery] = useState('');
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth() + 1);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [variablePayByEmployeeId, setVariablePayByEmployeeId] = useState({}); // employeeId -> string
  // employeeId -> { credited } for everyone already processed in the selected month/year.
  const [processedRecords, setProcessedRecords] = useState({});
  const [processedLoadedFor, setProcessedLoadedFor] = useState('');
  const [defaultsAppliedFor, setDefaultsAppliedFor] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [running, setRunning] = useState(false);
  const [downloadMessage, setDownloadMessage] = useState('');
  const [runError, setRunError] = useState('');

  const monthKey = `${selectedYear}-${selectedMonth}`;

  useEffect(() => {
    const loadEmployees = async () => {
      setLoading(true);
      try {
        const data = await getAllEmployees();
        setEmployees(data.filter(
          (employee) => (employee.role || '').toLowerCase() !== 'admin'
            && (employee.employeeStatus || '').toLowerCase() !== 'inactive'
        ));
        setError('');
      } catch (err) {
        console.error('Failed to load payroll employees:', err);
        setError('Unable to load payroll employees at this time.');
      } finally {
        setLoading(false);
      }
    };

    loadEmployees();
  }, []);

  // Who already has a payroll row for the selected month/year, and whether it's been credited.
  const loadProcessedRecords = async () => {
    try {
      const report = await getPayrollReport({ month: selectedMonth, year: selectedYear, page: 0, size: 5000 });
      const records = {};
      (report?.employees || []).forEach((record) => {
        records[String(record.employeeId)] = { credited: isCreditedStatus(record.creditStatus) };
      });
      setProcessedRecords(records);
      return records;
    } catch (err) {
      console.error('Failed to check already-processed employees:', err);
      setProcessedRecords({});
      return {};
    } finally {
      setProcessedLoadedFor(`${selectedYear}-${selectedMonth}`);
    }
  };

  useEffect(() => {
    setDownloadMessage('');
    setRunError('');
    loadProcessedRecords();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedMonth, selectedYear]);

  // Starting selection for a month, once both the employees and that month's processed list
  // are in: everyone who hasn't been processed yet.
  useEffect(() => {
    if (loading || processedLoadedFor !== monthKey || defaultsAppliedFor === monthKey) return;
    setIncludedEmployeeIds(employees
      .filter((employee) => !processedRecords[String(employee.id)])
      .map((employee) => String(employee.id)));
    setDefaultsAppliedFor(monthKey);
  }, [loading, employees, processedRecords, processedLoadedFor, defaultsAppliedFor, monthKey]);

  const isProcessed = (employee) => Boolean(processedRecords[String(employee.id)]);
  const isCredited = (employee) => Boolean(processedRecords[String(employee.id)]?.credited);
  // Only credited employees (already paid) are locked. A processed-but-not-credited employee can
  // be ticked again to re-run them; an employee without a salary can be ticked but the run
  // reports them as not processed until their CTC is filled in.
  const isSelectable = (employee) => !isCredited(employee);

  const handleToggleEmployee = (employeeId) => {
    const key = String(employeeId);
    setIncludedEmployeeIds((current) =>
      current.includes(key)
        ? current.filter((id) => id !== key)
        : [...current, key]
    );
  };

  const handleVariablePayChange = (employeeId, value) => {
    setVariablePayByEmployeeId((current) => ({ ...current, [String(employeeId)]: value }));
  };

  const handleRunPayroll = async () => {
    const employeesForPayroll = employees.filter((employee) =>
      effectiveIncludedIds.includes(String(employee.id))
    );
    if (employeesForPayroll.length === 0) {
      setRunError('Tick at least one employee in Included Employees to run payroll.');
      return;
    }

    const payload = {
      employees: employeesForPayroll.map((employee) => ({
        employeeId: employee.id,
        employeeName: fullName(employee),
        // Blank = keep the variable pay from a previous run (0 for a first run).
        variablePay: (variablePayByEmployeeId[String(employee.id)] ?? '') === ''
          ? null
          : Number(variablePayByEmployeeId[String(employee.id)]) || 0,
      })),
      month: selectedMonth,
      year: selectedYear,
    };

    setRunning(true);
    setRunError('');
    setDownloadMessage('');

    try {
      const { fileBlob, filename } = await runPayroll(payload);
      const downloadUrl = window.URL.createObjectURL(fileBlob);
      const link = document.createElement('a');
      link.href = downloadUrl;
      link.download = filename || `payroll-${selectedMonth}-${selectedYear}.xlsx`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      setTimeout(() => window.URL.revokeObjectURL(downloadUrl), 1000);

      const records = await loadProcessedRecords();
      const failed = employeesForPayroll.filter((employee) => !records[String(employee.id)]);
      const processedCount = employeesForPayroll.length - failed.length;
      setDownloadMessage(`Payroll processed for ${processedCount} employee${processedCount === 1 ? '' : 's'} and the Excel downloaded. Go to Payroll Report to mark them credited.`);
      const noSalaryFailed = failed.filter((employee) => !hasSalary(employee));
      const otherFailed = failed.filter((employee) => hasSalary(employee));
      const problems = [];
      if (noSalaryFailed.length > 0) {
        problems.push(`${noSalaryFailed.map(fullName).join(', ')} - no CTC / basic salary set (fill it in the employee's Salary details)`);
      }
      if (otherFailed.length > 0) {
        problems.push(`${otherFailed.map(fullName).join(', ')} - see the Message column in the Excel`);
      }
      if (problems.length > 0) {
        setRunError(`Not processed: ${problems.join('; ')}.`);
      }
      // The employees just processed move to Excluded (marked Processed) so the next run -
      // today or another day - only picks up whoever is ticked then.
      setIncludedEmployeeIds((current) => current.filter((id) => !records[id]));
    } catch (err) {
      console.error('Failed to run payroll:', err);
      setRunError(err.message || 'Failed to run payroll');
    } finally {
      setRunning(false);
    }
  };

  // ID must match exactly (searching "1" should not also return 10, 11, 12...);
  // the rest stay substring matches since that's expected for text search.
  const matchesEmployeeSearch = (employee, query) => {
    if (!query) return true;
    // Exact match on the client-assigned Employee ID (e.g. "EMP001"), not the internal id.
    if (String(employee.employeeId ?? '').toLowerCase() === query) return true;

    return [
      fullName(employee),
      employee.email,
      employee.phone,
      employee.department,
      employee.designation,
    ].some((field) => field?.toString().toLowerCase().includes(query));
  };

  // Ticked employees that can actually be run (a credited employee is already paid).
  const effectiveIncludedIds = useMemo(
    () => includedEmployeeIds.filter((id) => !processedRecords[id]?.credited
      && employees.some((item) => String(item.id) === id)),
    [includedEmployeeIds, employees, processedRecords]
  );

  // Included/excluded employees are split from the full roster first, so the two
  // search boxes stay independent - text typed in one can't hide results in the other.
  const includedEmployeesUnfiltered = useMemo(
    () => employees.filter((employee) => effectiveIncludedIds.includes(String(employee.id))),
    [employees, effectiveIncludedIds]
  );

  const excludedEmployeesUnfiltered = useMemo(
    () => employees.filter((employee) => !effectiveIncludedIds.includes(String(employee.id))),
    [employees, effectiveIncludedIds]
  );

  const includedEmployees = useMemo(() => {
    const query = searchQuery.trim().toLowerCase();
    if (!query) return includedEmployeesUnfiltered;

    return includedEmployeesUnfiltered.filter((employee) => matchesEmployeeSearch(employee, query));
  }, [includedEmployeesUnfiltered, searchQuery]);

  const filteredExcludedEmployees = useMemo(() => {
    const query = excludedSearchQuery.trim().toLowerCase();
    if (!query) return excludedEmployeesUnfiltered;

    return excludedEmployeesUnfiltered.filter((employee) => matchesEmployeeSearch(employee, query));
  }, [excludedEmployeesUnfiltered, excludedSearchQuery]);

  const activeEmployeeCount = employees.length;
  const includedCount = effectiveIncludedIds.length;
  const excludedCount = Math.max(activeEmployeeCount - includedCount, 0);
  const processedCount = Object.keys(processedRecords).length;
  const creditedCount = Object.values(processedRecords).filter((record) => record.credited).length;
  const monthOptions = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December',
  ];
  const yearOptions = Array.from({ length: 5 }, (_, index) => new Date().getFullYear() - 2 + index);
  const selectedMonthName = monthOptions[selectedMonth - 1];
  const payrollReportRows = useMemo(() => {
    return employees
      .filter((employee) => effectiveIncludedIds.includes(String(employee.id)))
      .map((employee) => ({
        id: employee.id,
        employeeCode: employee.employeeId || 'N/A',
        name: fullName(employee),
        department: employee.department || 'N/A',
        designation: employee.designation || 'N/A',
        amount: employee.ctc || employee.basicSalary || 'N/A',
        variablePay: Number(variablePayByEmployeeId[String(employee.id)]) || 0,
        rerun: Boolean(processedRecords[String(employee.id)]),
      }));
  }, [employees, effectiveIncludedIds, variablePayByEmployeeId, processedRecords]);

  // "Select all" for one table: ticks (or unticks) every selectable row currently shown in it.
  const renderSelectAll = (rows, include) => {
    const selectableIds = rows.filter(isSelectable).map((employee) => String(employee.id));
    return (
      <label className="flex items-center gap-1.5 whitespace-nowrap">
        <input
          type="checkbox"
          checked={include ? false : selectableIds.length > 0 && selectableIds.every((id) => effectiveIncludedIds.includes(id))}
          disabled={selectableIds.length === 0}
          onChange={() => setIncludedEmployeeIds((current) => (include
            ? [...new Set([...current, ...selectableIds])]
            : current.filter((id) => !selectableIds.includes(id))))}
          className="size-4 rounded border-border accent-client"
        />
        {include ? 'Select All' : 'Unselect All'}
      </label>
    );
  };

  const renderPayrollTable = (rows, emptyMessage, isIncludedTable) => {
    if (rows.length === 0) {
      return <p className="px-5 py-6 text-sm text-muted-foreground">{emptyMessage}</p>;
    }

    const columns = ['Employee ID', 'Name', 'Email', 'Department', 'Designation', 'Phone', 'Salary'];
    if (isIncludedTable) columns.push('Variable Pay');

    return (
      <div className="overflow-x-auto">
        <table className="w-full text-left" style={{ minWidth: isIncludedTable ? 980 : 860 }}>
          <thead>
            <tr className="border-b border-border/80 bg-muted/40">
              <th className="h-11 whitespace-nowrap px-4 text-[11px] font-semibold uppercase tracking-[0.06em] text-muted-foreground">
                {renderSelectAll(rows, !isIncludedTable)}
              </th>
              {columns.map((col) => (
                <th key={col} className="h-11 whitespace-nowrap px-4 text-[11px] font-semibold uppercase tracking-[0.06em] text-muted-foreground">
                  {col}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((employee) => {
              const employeeId = String(employee.id);
              const processed = isProcessed(employee);
              const credited = isCredited(employee);
              const noSalary = !hasSalary(employee);
              const isIncluded = effectiveIncludedIds.includes(employeeId);

              return (
                <tr key={employee.id} className={`border-b border-border/60 last:border-0 hover:bg-muted/30 ${!isIncluded && credited ? 'opacity-60' : ''}`}>
                  <td className="px-4 py-3">
                    <input
                      type="checkbox"
                      checked={isIncluded}
                      disabled={!isSelectable(employee)}
                      title={credited ? 'Already credited - locked' : processed ? 'Tick to run payroll again for this employee' : 'Tick to include in payroll'}
                      onChange={() => handleToggleEmployee(employee.id)}
                      className="size-4 rounded border-border accent-client disabled:cursor-not-allowed"
                    />
                    {processed && (
                      <span className={`mt-0.5 flex items-center gap-0.5 text-[10px] font-semibold ${credited ? 'text-[#15803d]' : 'text-[#1d4ed8]'}`}>
                        <CheckCircle2 className="size-3" />
                        {credited ? 'Credited' : 'Processed'}
                      </span>
                    )}
                    {!processed && noSalary && (
                      <span className="mt-0.5 block text-[10px] font-semibold text-[#b91c1c]" title="Fill in the CTC / basic salary in the employee's profile">
                        No salary set
                      </span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-sm text-foreground">{employee.employeeId || 'N/A'}</td>
                  <td className="px-4 py-3 text-sm text-foreground">{fullName(employee)}</td>
                  <td className="px-4 py-3 text-sm text-muted-foreground">{employee.email || 'N/A'}</td>
                  <td className="px-4 py-3 text-sm text-muted-foreground">{employee.department || 'N/A'}</td>
                  <td className="px-4 py-3 text-sm text-muted-foreground">{employee.designation || 'N/A'}</td>
                  <td className="px-4 py-3 text-sm text-muted-foreground">{employee.phone || 'N/A'}</td>
                  <td className="px-4 py-3 text-sm text-muted-foreground">{employee.ctc || employee.basicSalary || 'N/A'}</td>
                  {isIncludedTable && (
                    <td className="px-4 py-3">
                      <input
                        type="number"
                        min="0"
                        step="0.01"
                        placeholder="0.00"
                        value={variablePayByEmployeeId[employeeId] ?? ''}
                        onChange={(e) => handleVariablePayChange(employeeId, e.target.value)}
                        className="h-9 w-28 rounded-lg border border-border bg-white px-2.5 text-sm outline-none focus:border-client focus:ring-2 focus:ring-client/30"
                      />
                    </td>
                  )}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    );
  };

  return (
    <AdminLayout userName={userName} onLogout={onLogout} activeItem="payroll" title="Payroll" subtitle="Search active employees, then use the checkbox to include or exclude them from payroll.">
      <div className="flex flex-col gap-5">
        <section className="grid grid-cols-2 gap-4 lg:grid-cols-4">
          {[
            { label: 'Active Employees', value: activeEmployeeCount },
            { label: 'Included', value: includedCount },
            { label: 'Excluded', value: excludedCount },
            { label: 'Processed / Credited', value: `${processedCount} / ${creditedCount}` },
          ].map((item) => (
            <div key={item.label} className="rounded-xl border border-border/80 bg-card p-4 text-center shadow-sm">
              <div className="text-2xl font-semibold tabular-nums tracking-tight text-foreground">{item.value}</div>
              <div className="mt-1 text-xs text-muted-foreground">{item.label}</div>
            </div>
          ))}
        </section>

        <section className="flex flex-col gap-5">
          <div className="flex flex-wrap items-end gap-4 rounded-xl border border-border/80 bg-card p-4 shadow-sm">
            <div className="flex flex-col gap-1.5">
              <label className="text-sm font-medium text-foreground">Month</label>
              <select
                value={selectedMonth}
                onChange={(e) => setSelectedMonth(Number(e.target.value))}
                className="h-9 rounded-lg border border-border bg-white px-2.5 text-sm outline-none focus:border-client focus:ring-2 focus:ring-client/30"
              >
                {monthOptions.map((month, index) => (
                  <option key={month} value={index + 1}>
                    {month}
                  </option>
                ))}
              </select>
            </div>
            <div className="flex flex-col gap-1.5">
              <label className="text-sm font-medium text-foreground">Year</label>
              <select
                value={selectedYear}
                onChange={(e) => setSelectedYear(Number(e.target.value))}
                className="h-9 rounded-lg border border-border bg-white px-2.5 text-sm outline-none focus:border-client focus:ring-2 focus:ring-client/30"
              >
                {yearOptions.map((year) => (
                  <option key={year} value={year}>
                    {year}
                  </option>
                ))}
              </select>
            </div>
            <p className="max-w-xl text-sm text-muted-foreground">
              Tick the employees to pay and click Run - you can run again any time (e.g. some employees today,
              the rest tomorrow). Pay is calculated from attendance; mark it credited in Payroll Report.
            </p>
            <div className="ml-auto flex items-center gap-3">
              <button
                type="button"
                onClick={handleRunPayroll}
                disabled={running}
                className="h-9 rounded-lg bg-client px-4 text-sm font-medium text-client-foreground hover:bg-client/90 disabled:opacity-60"
              >
                {running ? 'Running...' : `Run${includedCount ? ` (${includedCount})` : ''}`}
              </button>
            </div>
          </div>

          {loading ? (
            <p className="text-sm text-muted-foreground">Loading payroll data...</p>
          ) : error ? (
            <p className="text-sm text-[#b91c1c]">{error}</p>
          ) : (
            <>
              {downloadMessage && (
                <div className="rounded-lg border border-[#bbf7d0] bg-[#f0fdf4] px-3 py-2 text-sm text-[#15803d]">{downloadMessage}</div>
              )}
              {runError && (
                <div className="rounded-lg border border-[#fecaca] bg-[#fef2f2] px-3 py-2 text-sm text-[#b91c1c]">{runError}</div>
              )}

              <div className="rounded-xl border border-border/80 bg-card shadow-sm">
                <div className="flex flex-wrap items-center justify-between gap-3 border-b border-border/80 px-5 py-4">
                  <div>
                    <h3 className="text-base font-semibold text-foreground">Payroll Report</h3>
                    <p className="mt-0.5 text-sm text-muted-foreground">
                      Showing selected employees for {selectedMonthName} {selectedYear}.
                    </p>
                  </div>
                  <span className="text-sm text-muted-foreground">{payrollReportRows.length} employees</span>
                </div>

                {payrollReportRows.length === 0 ? (
                  <p className="px-5 py-6 text-sm text-muted-foreground">No employees selected for this payroll report.</p>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full text-left" style={{ minWidth: 900 }}>
                      <thead>
                        <tr className="border-b border-border/80 bg-muted/40">
                          {['Employee ID', 'Employee Name', 'Month', 'Year', 'Department', 'Designation', 'Amount', 'Variable Pay'].map((col) => (
                            <th key={col} className="h-11 whitespace-nowrap px-4 text-[11px] font-semibold uppercase tracking-[0.06em] text-muted-foreground">
                              {col}
                            </th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {payrollReportRows.map((row) => (
                          <tr key={`${row.id}-${selectedMonth}-${selectedYear}`} className="border-b border-border/60 last:border-0 hover:bg-muted/30">
                            <td className="px-4 py-3 text-sm text-foreground">
                              {row.employeeCode}
                              {row.rerun && <span className="mt-0.5 block text-[10px] font-semibold text-[#1d4ed8]">Re-run</span>}
                            </td>
                            <td className="px-4 py-3 text-sm text-foreground">{row.name}</td>
                            <td className="px-4 py-3 text-sm text-muted-foreground">{selectedMonthName}</td>
                            <td className="px-4 py-3 text-sm text-muted-foreground">{selectedYear}</td>
                            <td className="px-4 py-3 text-sm text-muted-foreground">{row.department}</td>
                            <td className="px-4 py-3 text-sm text-muted-foreground">{row.designation}</td>
                            <td className="px-4 py-3 text-sm text-muted-foreground">{row.amount}</td>
                            <td className="px-4 py-3 text-sm text-muted-foreground">{row.variablePay ? row.variablePay.toFixed(2) : '—'}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>

              <div className="rounded-xl border border-border/80 bg-card shadow-sm">
                <div className="flex flex-wrap items-center justify-between gap-3 border-b border-border/80 px-5 py-4">
                  <h3 className="text-base font-semibold text-foreground">Included Employees</h3>
                  <span className="text-sm text-muted-foreground">{includedEmployees.length} employees</span>
                </div>
                <div className="px-5 pt-4">
                  <div className="relative max-w-md">
                    <Search className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" />
                    <input
                      type="text"
                      placeholder="Search by employee ID, name, email, department, or designation"
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      className="h-9 w-full rounded-lg border border-border bg-white pl-9 pr-3 text-sm outline-none focus:border-client focus:ring-2 focus:ring-client/30"
                    />
                  </div>
                </div>
                {renderPayrollTable(includedEmployees, 'No included employees match your search.', true)}
              </div>

              <div className="rounded-xl border border-border/80 bg-card shadow-sm">
                <div className="flex flex-wrap items-center justify-between gap-3 border-b border-border/80 px-5 py-4">
                  <h3 className="text-base font-semibold text-foreground">Excluded Employees</h3>
                  <span className="text-sm text-muted-foreground">{filteredExcludedEmployees.length} employees</span>
                </div>
                <div className="px-5 pt-4">
                  <div className="relative max-w-md">
                    <Search className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" />
                    <input
                      type="text"
                      placeholder="Search excluded employees"
                      value={excludedSearchQuery}
                      onChange={(e) => setExcludedSearchQuery(e.target.value)}
                      className="h-9 w-full rounded-lg border border-border bg-white pl-9 pr-3 text-sm outline-none focus:border-client focus:ring-2 focus:ring-client/30"
                    />
                  </div>
                </div>
                {renderPayrollTable(filteredExcludedEmployees, 'No excluded employees match your search.', false)}
              </div>
            </>
          )}
        </section>
      </div>
    </AdminLayout>
  );
}

export default PayrollPage;
